import { useTranslation } from "react-i18next";
import { Button, Skeleton } from "@nexus/ui";
import { AlertCircle, Lock } from "lucide-react";
import { ApiError } from "@/lib/api-client";
import { iamQueryErrorMessage } from "@/features/identity-hub/lib/iam-query-error";

const FORBIDDEN_STATUS = 403;

interface IamListErrorProps {
  error: unknown;
  onRetry: () => void;
}

export function IamListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-md" />
      ))}
    </div>
  );
}

export function IamListError({ error, onRetry }: IamListErrorProps) {
  const { t } = useTranslation();
  if (error instanceof ApiError && error.status === FORBIDDEN_STATUS) return <IamListForbidden />;
  const message = iamQueryErrorMessage(
    error,
    t("iam.dashboard.batchLoadError", "Failed to load access data."),
  );

  return (
    <div
      role="alert"
      className="flex min-h-full flex-col items-center justify-center gap-2 border-t border-destructive/20 bg-card py-16 text-center"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-5 w-5 text-destructive" />
      </div>
      <p className="text-sm font-medium">
        {t("iam.dashboard.loadError.title", "Couldn't load this list")}
      </p>
      <p className="max-w-md text-xs text-muted-foreground">
        {t(
          "iam.dashboard.loadError.body",
          "This tab has no rows because the request failed. Retry, or check that your account is allowed to view this list.",
        )}
      </p>
      <p className="max-w-md text-xs text-destructive">{message}</p>
      <Button type="button" variant="outline" size="sm" className="mt-1" onClick={onRetry}>
        {t("iam.dashboard.retry", "Retry")}
      </Button>
    </div>
  );
}

/** A missing grant is not a failure to retry: say what is going on instead of echoing the API's RBAC text. */
function IamListForbidden() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-2 border-t border-border bg-card py-16 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
        <Lock className="h-5 w-5 text-muted-foreground" aria-hidden />
      </div>
      <p className="text-sm font-medium">
        {t("iam.dashboard.forbidden.title", "You don't have access to this list")}
      </p>
      <p className="max-w-md text-xs text-muted-foreground">
        {t(
          "iam.dashboard.forbidden.body",
          "Your role doesn't include viewing it. Ask a platform admin if you need it.",
        )}
      </p>
    </div>
  );
}
